import { ApiErrorDTO } from "@bvhi/backend-api/apis";
import { useCallback } from "react";

import { useTranslation } from "react-i18next";

export const useErrorObjectMapper = () => {
  const { t } = useTranslation(["errors"]);

  const getErrorMessageFromErrorCode = useCallback(
    (errorCode: string): string | undefined => {
      switch (errorCode) {
        case "ERR_USERS_TERMS_OF_SERVICE_CONSENT_REQUIRED":
          return t("errors:custom.error.code.ERR_USERS_TERMS_OF_SERVICE_CONSENT_REQUIRED");
        case "ERR_VALUE_INVALID_EMAIL":
          return t("errors:custom.error.code.ERR_VALUE_INVALID_EMAIL");
        case "ERR_USERS_INVALID_ORGANISATION_CODE":
          return t("errors:custom.error.code.ERR_USERS_INVALID_ORGANISATION_CODE");
        case "ERR_USERS_INVALID_ORGANISATION_NAME":
          return t("errors:custom.error.code.ERR_USERS_INVALID_ORGANISATION_NAME");
        case "ERR_USERS_INVALID_PASSWORD":
          return t("errors:custom.error.code.ERR_USERS_INVALID_PASSWORD");
        case "ERR_USERS_INVALID_USERNAME":
          return t("errors:custom.error.code.ERR_USERS_INVALID_USERNAME");
        case "ERR_POINT_OF_SALE_ID_UNKNOWN":
          return t("errors:custom.error.code.ERR_POINT_OF_SALE_ID_UNKNOWN");
        case "ERR_VERIFICATION_CODE_INVALID_CODE":
          return t("errors:custom.error.code.ERR_VERIFICATION_CODE_INVALID_CODE");
        case "ERR_USERS_NO_ORGANISATION_NAME":
          return t("errors:custom.error.code.ERR_USERS_NO_ORGANISATION_NAME");
        case "ERR_USERS_TYPE_REQUIRED":
          return t("errors:custom.error.code.ERR_USERS_TYPE_REQUIRED");
        case "ERR_USERS_USERNAME_TAKEN":
          return t("errors:custom.error.code.ERR_USERS_USERNAME_TAKEN");
        case "ERR_VERIFICATION_CODE_ALREADY_USED_CODE_FROM_BATCH":
          return t("errors:custom.error.code.ERR_VERIFICATION_CODE_ALREADY_USED_CODE_FROM_BATCH");
        case "ERR_VERIFICATION_CODE_CHAIN_NUMBER_USED_IN_OTHER_INVITE":
          return t("errors:custom.error.code.ERR_VERIFICATION_CODE_CHAIN_NUMBER_USED_IN_OTHER_INVITE");
        case "ERR_VERIFICATION_CODE_CUSTOMER_RECENTLY_INVITED":
          return t("errors:custom.error.code.ERR_VERIFICATION_CODE_CUSTOMER_RECENTLY_INVITED");
        case "ERR_SCL_PRIMARY_VALIDATION_FAILED":
          return t("errors:custom.error.code.ERR_SCL_PRIMARY_VALIDATION_FAILED");
        case "ERR_SCL_SECONDARY_VALIDATION_FAILED":
          return t("errors:custom.error.code.ERR_SCL_SECONDARY_VALIDATION_FAILED");
        case "ERR_SCL_PRIMARY_VALIDATION_ALREADY_RUNNING":
          return t("errors:custom.error.code.ERR_SCL_PRIMARY_VALIDATION_ALREADY_RUNNING");
        case "ERR_SCL_SECONDARY_VALIDATION_ALREADY_RUNNING":
          return t("errors:custom.error.code.ERR_SCL_SECONDARY_VALIDATION_ALREADY_RUNNING");
        case "ERR_SCL_PRIMARY_SYNCHRONISATION_ALREADY_RUNNING":
          return t("errors:custom.error.code.ERR_SCL_PRIMARY_SYNCHRONISATION_ALREADY_RUNNING");
        case "ERR_SCL_SECONDARY_SYNCHRONISATION_ALREADY_RUNNING":
          return t("errors:custom.error.code.ERR_SCL_SECONDARY_SYNCHRONISATION_ALREADY_RUNNING");
        case "ERR_SCL_CHAIN_NUMBER_ALREADY_REGISTERED":
          return t("errors:custom.error.code.ERR_SCL_CHAIN_NUMBER_ALREADY_REGISTERED");
        case "ERR_SCL_CHAIN_NUMBER_UNKNOWN":
          return t("errors:custom.error.code.ERR_SCL_CHAIN_NUMBER_UNKNOWN");
        case "ERR_GENERAL_409":
          return t("errors:custom.error.code.ERR_GENERAL_409");
        default:
          return undefined;
      }
    },
    [t]
  );

  const getErrorMessagesFromErrorObject = (error: ApiErrorDTO): string[] => {
    const errorMessages: string[] = [];
    // try to get custom error messages
    if (error.errors) {
      error.errors.forEach((err) => {
        const value = getErrorMessageFromErrorCode(err.code);
        if (value) errorMessages.push(value);
      });
    }
    // try to get general error message if no custom errors could be found
    if (errorMessages.length === 0) {
      const value = getErrorMessageFromErrorCode(error.code);
      if (value) errorMessages.push(value);
    }

    // default message if nothing could be found
    if (errorMessages.length === 0) {
      errorMessages.push(t("custom.error.code.ERR_DEFAULT"));
    }
    return errorMessages;
  };

  return { getErrorMessagesFromErrorObject };
};
